import { Trans } from '@lingui/macro'
import { Box, Step, StepLabel, Stepper, Typography } from '@mui/material'
import { FC } from 'react'
import { EditableFormIcon } from '../forms/FormStepper'
import { StepperFeatStickyComponentController } from '../forms/StepperFeatStickyComponentController'
import FormStepperDropdown from '../forms/components/form-stepper/form-stepper-dropdown'
import { HEADER } from '../opportunity/opportunityViewComponents'

interface SurveyTabStepperProps {
  currentStep: number
  stepperRef: React.RefObject<HTMLDivElement>
  data: any[]
  disableFields: string[]
  setCurrentStep: (step: number) => void
  sectionToErrors: Record<string, boolean>
  renderDropdown?: boolean
}

const SurveyTabStepper: FC<SurveyTabStepperProps> = ({
  currentStep,
  stepperRef,
  data,
  disableFields,
  setCurrentStep,
  sectionToErrors,
  renderDropdown
}) => {
  const filteredData = data.filter(template => {
    const notAllDisabled = template.questions.some(question => {
      return (
        !disableFields.includes(question.id) &&
        question.type !== HEADER
      )
    })
    if (
      template.questions.length === 0 ||
      disableFields.includes(template.templateId) ||
      !notAllDisabled
    ) {
      return false
    }
    return true
  })

  if (renderDropdown) {
    return (
      <Box>
        <Typography variant='body2' sx={{ fontSize: '14px', lineHeight: '19px', fontWeight: 400, mb: '8px' }}>
          <Trans>FORM_STEPPER_CURRENT_SECTION</Trans>
        </Typography>
        <FormStepperDropdown
          renderIcons
          sectionToErrors={sectionToErrors}
          currentStep={currentStep}
          disabled={false}
          handleStepChange={setCurrentStep}
          sections={filteredData.map((data, index) => ({ ...data, trueIndex: index }))}
          renderStepLabel={(index, section) => section.header}
        />
      </Box>
    )
  }

  return (
    <Stepper
      alternativeLabel
      nonLinear
      activeStep={currentStep}
      ref={stepperRef}
      orientation='horizontal'
    >
      {filteredData.map((template, index) => {
        return (
          <Step
            key={index}
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setCurrentStep(index)
            }}
            completed={index < currentStep}
          >
            <StepLabel
              error={sectionToErrors[template.templateId]}
              StepIconComponent={EditableFormIcon}
              sx={{ fontWeight: currentStep === index ? 'bold' : 400 }}
            >
              {template.header}
            </StepLabel>
          </Step>
        )
      })}
    </Stepper>
  )
}

type SurveyTabStepperFeatStickyComponentControllerProps = {
  stickyComponentRef?: React.RefObject<HTMLDivElement>
  isStickyCallback?: (isSticky: boolean) => void
} & SurveyTabStepperProps

/**
 * This component decides how to render base SurveyTabStepper depending on whether the stickyComponentRef element is sticky or not.
 * @param {RefObject<HTMLElement>} props.stickyComponentRef - The ref for the sticky component.
 * @param {Function} props.isStickyCallback - Callback function to notify when the sticky state changes.
 * @param {ReactElement} props - base SurveyTabStepper props
 * @returns {ReactElement} The rendered component.
 */
const SurveyTabStepperFeatStickyComponentController: FC<SurveyTabStepperFeatStickyComponentControllerProps> =
  ({ stickyComponentRef, isStickyCallback, ...props }) => {
    return (
      <StepperFeatStickyComponentController
        stickyComponentRef={stickyComponentRef}
        isStickyCallback={isStickyCallback}
      >
        <SurveyTabStepper {...props} />
      </StepperFeatStickyComponentController>
    )
  }

export { SurveyTabStepperFeatStickyComponentController as SurveyTabStepper }

